import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, useMapEvents, useMap } from 'react-leaflet';
import { DivIcon, LatLngBounds } from 'leaflet';
import { List, X, Crosshair, SlidersHorizontal, Loader2, AlertCircle, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { MapFilterPanel } from '../components/listings/MapFilterPanel';
import { Spinner } from '../components/ui/Spinner';
import { toast } from '../components/ui/Toast';
import { useListingsStore } from '../stores/listings';
import { formatPrice, cn } from '../lib/utils';
import type { MapListing } from '../services/api';

// Default map center (Italy)
const DEFAULT_CENTER: [number, number] = [41.9028, 12.4964];
const DEFAULT_ZOOM = 6;

// City coordinates for centering the map
const CITY_COORDINATES: Record<string, { lat: number; lng: number; zoom: number }> = {
  'Milano': { lat: 45.4642, lng: 9.1900, zoom: 12 },
  'Roma': { lat: 41.9028, lng: 12.4964, zoom: 12 },
  'Napoli': { lat: 40.8518, lng: 14.2681, zoom: 12 },
  'Torino': { lat: 45.0703, lng: 7.6869, zoom: 12 },
  'Bologna': { lat: 44.4949, lng: 11.3426, zoom: 12 },
  'Firenze': { lat: 43.7696, lng: 11.2558, zoom: 12 },
  'Genova': { lat: 44.4056, lng: 8.9463, zoom: 12 },
  'Palermo': { lat: 38.1157, lng: 13.3615, zoom: 12 },
  'Bari': { lat: 41.1171, lng: 16.8719, zoom: 12 },
  'Catania': { lat: 37.5079, lng: 15.0830, zoom: 12 },
  'Venezia': { lat: 45.4408, lng: 12.3155, zoom: 13 },
  'Verona': { lat: 45.4384, lng: 10.9916, zoom: 12 },
  'Padova': { lat: 45.4064, lng: 11.8768, zoom: 12 },
};

type SheetState = 'peek' | 'half' | 'full';

// Custom price marker
function createPriceMarker(listing: MapListing, isSelected: boolean) {
  const priceText = listing.price
    ? `€${listing.price.toLocaleString('it-IT')}`
    : '?';

  return new DivIcon({
    className: 'price-marker-container',
    html: `
      <div class="price-marker ${isSelected ? 'active' : ''}">
        ${priceText}
      </div>
    `,
    iconSize: [90, 30],
    iconAnchor: [45, 30],
  });
}

// Map event handler component - handles bounds changes and triggers data fetching
function MapEvents({
  onBoundsChange,
}: {
  onBoundsChange: (bounds: LatLngBounds) => void;
}) {
  const map = useMapEvents({
    moveend: () => {
      onBoundsChange(map.getBounds());
    },
    zoomend: () => {
      onBoundsChange(map.getBounds());
    },
  });

  // Trigger initial bounds fetch on mount
  useEffect(() => {
    // Small delay to ensure map is fully initialized
    const timer = setTimeout(() => {
      onBoundsChange(map.getBounds());
    }, 100);
    return () => clearTimeout(timer);
  }, [map, onBoundsChange]);

  return null;
}

// Component to handle flying to a city when filters change
function FlyToCity({ city }: { city: string | undefined }) {
  const map = useMap();

  useEffect(() => {
    if (city) {
      const coords = CITY_COORDINATES[city.trim()];
      if (coords) {
        map.flyTo([coords.lat, coords.lng], coords.zoom);
      }
    }
  }, [city, map]);

  return null;
}

// Center map button component
function CenterMapButton({ onCenter }: { onCenter: () => void }) {
  return (
    <button
      onClick={onCenter}
      className="h-12 w-12 min-w-[48px] min-h-[48px] rounded-full bg-white shadow-lg flex items-center justify-center hover:bg-gray-50 active:scale-95 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
      aria-label="Centra posizione"
    >
      <Crosshair className="h-5 w-5 text-gray-700" />
    </button>
  );
}

// Error banner component
function ErrorBanner({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[1000] bg-white rounded-lg shadow-lg p-4 max-w-sm w-full mx-4">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0">
          <AlertCircle className="h-5 w-5 text-red-500" />
        </div>
        <div className="flex-1">
          <p className="text-sm text-gray-700">{message}</p>
          <Button
            variant="outline"
            size="sm"
            className="mt-2"
            onClick={onRetry}
          >
            <RefreshCw className="mr-2 h-3 w-3" />
            Riprova
          </Button>
        </div>
        <button
          onClick={onRetry}
          className="flex-shrink-0 p-1 hover:bg-gray-100 rounded"
          aria-label="Chiudi"
        >
          <X className="h-4 w-4 text-gray-400" />
        </button>
      </div>
    </div>
  );
}

// Selected listing panel for mobile
function SelectedListingPanelMobile({
  listing,
  onClose,
}: {
  listing: MapListing;
  onClose: () => void;
}) {
  return (
    <div className="absolute bottom-[120px] left-4 right-4 z-[1000] md:hidden">
      <Card className="overflow-hidden shadow-lg">
        {listing.imageUrl && (
          <div className="w-full h-36">
            <img src={listing.imageUrl} alt="" className="w-full h-full object-cover" loading="lazy" />
          </div>
        )}
        <div className="p-4">
          <div className="flex items-start justify-between mb-2">
            <span
              className={cn(
                'rounded px-2 py-0.5 text-xs font-medium',
                listing.contractType === 'SALE'
                  ? 'bg-blue-500 text-white'
                  : 'bg-success text-white'
              )}
            >
              {listing.contractType === 'SALE' ? 'Vendita' : 'Affitto'}
            </span>
            <button
              onClick={onClose}
              className="min-w-[44px] min-h-[44px] p-2 hover:bg-gray-100 rounded-lg flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
              aria-label="Chiudi"
            >
              <X className="h-4 w-4 text-gray-500" />
            </button>
          </div>

          <h3 className="font-semibold text-gray-900 line-clamp-2 mb-1">
            {listing.title}
          </h3>

          {(listing.surface || listing.rooms) && (
            <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
              {listing.surface && <span>{listing.surface} m²</span>}
              {listing.surface && listing.rooms && <span>·</span>}
              {listing.rooms && <span>{listing.rooms} locali</span>}
            </div>
          )}

          {listing.price ? (
            <p className="text-lg font-bold text-gray-900 mb-3">
              {formatPrice(listing.price, listing.contractType)}
            </p>
          ) : (
            <p className="text-sm text-gray-500 mb-3">Prezzo su richiesta</p>
          )}

          <Link to={`/listings/${listing.id}`}>
            <Button className="w-full" size="sm">
              Vedi dettagli
            </Button>
          </Link>
        </div>
      </Card>
    </div>
  );
}

// Selected listing panel for desktop
function SelectedListingPanelDesktop({
  listing,
  onClose,
}: {
  listing: MapListing;
  onClose: () => void;
}) {
  return (
    <div className="absolute bottom-4 left-4 z-[1000] hidden md:block">
      <Card className="overflow-hidden shadow-lg w-80">
        {listing.imageUrl && (
          <div className="w-full h-40">
            <img src={listing.imageUrl} alt="" className="w-full h-full object-cover" loading="lazy" />
          </div>
        )}
        <div className="p-4">
          <div className="flex items-start justify-between mb-2">
            <span
              className={cn(
                'rounded px-2 py-0.5 text-xs font-medium',
                listing.contractType === 'SALE'
                  ? 'bg-blue-500 text-white'
                  : 'bg-success text-white'
              )}
            >
              {listing.contractType === 'SALE' ? 'Vendita' : 'Affitto'}
            </span>
            <button
              onClick={onClose}
              className="min-w-[44px] min-h-[44px] p-2 hover:bg-gray-100 rounded-lg flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
              aria-label="Chiudi"
            >
              <X className="h-4 w-4 text-gray-500" />
            </button>
          </div>

          <h3 className="font-semibold text-gray-900 line-clamp-2 mb-1">
            {listing.title}
          </h3>

          {(listing.surface || listing.rooms) && (
            <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
              {listing.surface && <span>{listing.surface} m²</span>}
              {listing.surface && listing.rooms && <span>·</span>}
              {listing.rooms && <span>{listing.rooms} locali</span>}
            </div>
          )}

          {listing.price ? (
            <p className="text-lg font-bold text-gray-900 mb-3">
              {formatPrice(listing.price, listing.contractType)}
            </p>
          ) : (
            <p className="text-sm text-gray-500 mb-3">Prezzo su richiesta</p>
          )}

          <Link to={`/listings/${listing.id}`}>
            <Button className="w-full" size="sm">
              Vedi dettagli
            </Button>
          </Link>
        </div>
      </Card>
    </div>
  );
}

// Simple listing card for bottom sheet
function SimpleListingCard({
  listing,
  isSelected,
  onClick,
}: {
  listing: MapListing;
  isSelected: boolean;
  onClick: () => void;
}) {
  return (
    <div
      onClick={onClick}
      className={cn(
        'rounded-xl border cursor-pointer transition-all overflow-hidden',
        isSelected
          ? 'border-blue-500 bg-blue-50'
          : 'border-gray-200 bg-white hover:border-gray-300'
      )}
    >
      <div className="flex">
        {listing.imageUrl && (
          <div className="w-24 h-24 flex-shrink-0">
            <img
              src={listing.imageUrl}
              alt=""
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        )}
        <div className="flex-1 p-3 min-w-0">
          <div className="flex items-start justify-between mb-1">
            <span
              className={cn(
                'rounded px-1.5 py-0.5 text-[10px] font-medium',
                listing.contractType === 'SALE'
                  ? 'bg-blue-500 text-white'
                  : 'bg-success text-white'
              )}
            >
              {listing.contractType === 'SALE' ? 'Vendita' : 'Affitto'}
            </span>
            {listing.price ? (
              <span className="font-bold text-sm text-gray-900">
                {formatPrice(listing.price, listing.contractType)}
              </span>
            ) : (
              <span className="text-xs text-gray-500">Su richiesta</span>
            )}
          </div>
          <h3 className="text-xs font-medium text-gray-900 line-clamp-2 mb-1">
            {listing.title}
          </h3>
          {(listing.surface || listing.rooms) && (
            <div className="flex items-center gap-2 text-[11px] text-gray-500">
              {listing.surface && <span>{listing.surface} m²</span>}
              {listing.surface && listing.rooms && <span>·</span>}
              {listing.rooms && <span>{listing.rooms} locali</span>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Active filters badge component
function ActiveFiltersBadge({ count }: { count: number }) {
  if (count === 0) return null;
  return (
    <span className="ml-2 rounded-full bg-blue-500 px-2 py-0.5 text-xs font-semibold text-white">
      {count}
    </span>
  );
}

export default function MapView() {
  const {
    mapListings,
    isLoading,
    error,
    fetchMapListings,
    selectedListingId,
    setSelectedListing,
    clearError,
    filters,
  } = useListingsStore();

  const [mapBounds, setMapBounds] = useState<LatLngBounds | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [sheetState, setSheetState] = useState<SheetState>('peek');
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const sheetRef = useRef<HTMLDivElement>(null);
  const touchStartY = useRef<number>(0);
  const currentTranslateY = useRef<number>(0);
  const mapRef = useRef<any>(null);

  // Get initial map center from city filter
  const cityFilter = filters.city?.trim() || 'Milano';
  const cityCoords = CITY_COORDINATES[cityFilter];
  const initialCenter: [number, number] = cityCoords
    ? [cityCoords.lat, cityCoords.lng]
    : DEFAULT_CENTER;
  const initialZoom = cityCoords?.zoom || DEFAULT_ZOOM;

  // Count active filters for badge display
  const activeFiltersCount = useMemo(() => {
    return Object.entries(filters).filter(([key, value]) => {
      if (['page', 'limit', 'sortBy', 'sortOrder', 'liveSortBy'].includes(key)) return false;
      if (value === undefined || value === null || value === '') return false;
      if (key === 'city' && value === 'Milano') return false;
      return true;
    }).length;
  }, [filters]);

  // Debounce timer for map pan events
  const boundsChangeTimerRef = useRef<NodeJS.Timeout | null>(null);

  const handleBoundsChange = useCallback(
    (bounds: LatLngBounds) => {
      setMapBounds(bounds);

      // Debounce the API call to prevent excessive requests while panning
      if (boundsChangeTimerRef.current) {
        clearTimeout(boundsChangeTimerRef.current);
      }

      boundsChangeTimerRef.current = setTimeout(() => {
        fetchMapListings({
          north: bounds.getNorth(),
          south: bounds.getSouth(),
          east: bounds.getEast(),
          west: bounds.getWest(),
        });
      }, 300); // 300ms debounce
    },
    [fetchMapListings]
  );

  // Re-fetch when filters change (but bounds haven't)
  const prevFiltersRef = useRef<string>('');
  useEffect(() => {
    const { page, limit, ...restFilters } = filters;
    const filterKey = JSON.stringify(restFilters);

    if (prevFiltersRef.current !== filterKey && mapBounds) {
      prevFiltersRef.current = filterKey;
      fetchMapListings({
        north: mapBounds.getNorth(),
        south: mapBounds.getSouth(),
        east: mapBounds.getEast(),
        west: mapBounds.getWest(),
      });
    }
  }, [filters, mapBounds, fetchMapListings]);

  const handleRetry = useCallback(() => {
    clearError();
    if (mapBounds) {
      fetchMapListings({
        north: mapBounds.getNorth(),
        south: mapBounds.getSouth(),
        east: mapBounds.getEast(),
        west: mapBounds.getWest(),
      });
    }
  }, [clearError, fetchMapListings, mapBounds]);

  const selectedListing = useMemo(
    () => mapListings.find((l) => l.id === selectedListingId),
    [mapListings, selectedListingId]
  );

  // Calculate sheet translateY based on state
  const getSheetTranslateY = useCallback((state: SheetState) => {
    if (typeof window === 'undefined') return 0;
    const windowHeight = window.innerHeight;
    switch (state) {
      case 'peek':
        return windowHeight - 100;
      case 'half':
        return windowHeight * 0.5;
      case 'full':
        return windowHeight * 0.1;
      default:
        return windowHeight - 100;
    }
  }, []);

  // Touch handlers for bottom sheet
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    setIsDragging(true);
    touchStartY.current = e.touches[0].clientY;
    currentTranslateY.current = getSheetTranslateY(sheetState);
  }, [sheetState, getSheetTranslateY]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isDragging) return;
    const currentY = e.touches[0].clientY;
    const delta = currentY - touchStartY.current;
    setDragOffset(delta);
  }, [isDragging]);

  const handleTouchEnd = useCallback(() => {
    if (!isDragging) return;
    setIsDragging(false);

    const windowHeight = window.innerHeight;
    const currentY = currentTranslateY.current + dragOffset;

    // Calculate distances to each state
    const peekY = windowHeight - 100;
    const halfY = windowHeight * 0.5;
    const fullY = windowHeight * 0.1;

    const distToPeek = Math.abs(currentY - peekY);
    const distToHalf = Math.abs(currentY - halfY);
    const distToFull = Math.abs(currentY - fullY);

    // Snap to closest state
    const minDist = Math.min(distToPeek, distToHalf, distToFull);

    if (minDist === distToPeek) {
      setSheetState('peek');
    } else if (minDist === distToHalf) {
      setSheetState('half');
    } else {
      setSheetState('full');
    }

    setDragOffset(0);
  }, [isDragging, dragOffset]);

  // Handle center map on user location
  const handleCenterMap = useCallback(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          if (mapRef.current) {
            mapRef.current.flyTo([latitude, longitude], 14);
          }
        },
        (err) => {
          let message = 'Impossibile ottenere la posizione.';
          if (err.code === 1) {
            message = 'Permesso per la posizione negato.';
          } else if (err.code === 2) {
            message = 'Posizione non disponibile.';
          } else if (err.code === 3) {
            message = 'Timeout nel recupero della posizione.';
          }
          toast({ title: message, variant: 'warning' });
        }
      );
    } else {
      toast({ title: 'Geolocalizzazione non supportata dal browser.', variant: 'warning' });
    }
  }, []);

  // Get current translateY
  const sheetTranslateY = useMemo(() => {
    const baseY = getSheetTranslateY(sheetState);
    return isDragging ? baseY + dragOffset : baseY;
  }, [sheetState, isDragging, dragOffset, getSheetTranslateY]);

  // Handle listing click
  const handleListingClick = useCallback((listing: MapListing) => {
    setSelectedListing(listing.id);
    // Fly to listing on map
    if (mapRef.current) {
      mapRef.current.flyTo([listing.latitude, listing.longitude], 16);
    }
  }, [setSelectedListing]);

  return (
    <>
      {/* MOBILE LAYOUT */}
      <div className="md:hidden h-[100dvh] relative overflow-hidden bg-gray-100">
        {/* Map fullscreen */}
        <div className="absolute inset-0">
          <MapContainer
            center={initialCenter}
            zoom={initialZoom}
            className="h-full w-full"
            zoomControl={false}
            ref={mapRef}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            <MapEvents onBoundsChange={handleBoundsChange} />
            <FlyToCity city={filters.city} />

            {mapListings.map((listing, index) => (
              <Marker
                key={listing.id || `map-listing-${index}`}
                position={[listing.latitude, listing.longitude]}
                icon={createPriceMarker(listing, listing.id === selectedListingId)}
                eventHandlers={{
                  click: () => setSelectedListing(listing.id),
                }}
              >
                <Popup>
                  <div className="min-w-[200px]">
                    <h3 className="font-semibold text-sm mb-1 line-clamp-2">
                      {listing.title}
                    </h3>
                    {listing.price ? (
                      <p className="font-bold text-blue-500">
                        {formatPrice(listing.price, listing.contractType)}
                      </p>
                    ) : (
                      <p className="text-sm text-gray-500">Prezzo su richiesta</p>
                    )}
                    <Link
                      to={`/listings/${listing.id}`}
                      className="text-sm text-blue-500 hover:underline mt-2 block"
                    >
                      Vedi dettagli
                    </Link>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>

        {/* Error banner */}
        {error && (
          <ErrorBanner message={error} onRetry={handleRetry} />
        )}

        {/* Filter toggle - top left */}
        <div className="absolute top-4 left-4 z-[1000] flex gap-2">
          <Button
            variant="secondary"
            className="shadow-md bg-white"
            onClick={() => setShowFilters(!showFilters)}
          >
            <SlidersHorizontal className="mr-2 h-4 w-4" />
            Filtri
            <ActiveFiltersBadge count={activeFiltersCount} />
          </Button>
          <Link to="/listings">
            <Button variant="secondary" className="shadow-md bg-white">
              <List className="mr-2 h-4 w-4" />
              Lista
            </Button>
          </Link>
        </div>

        {/* Loading indicator - top right */}
        {isLoading && (
          <div className="absolute top-4 right-4 z-[1000] bg-white rounded-md shadow-md px-3 py-2 flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
            <span className="text-sm text-gray-600">Caricamento...</span>
          </div>
        )}

        {/* Results count - below loading or top right if not loading */}
        {!isLoading && !error && (
          <div className="absolute top-4 right-4 z-[1000] bg-white rounded-md shadow-md px-3 py-2">
            <span className="text-sm text-gray-600">
              {mapListings.length} {mapListings.length === 1 ? 'annuncio' : 'annunci'}
            </span>
          </div>
        )}

          {/* Filters panel with backdrop */}
          {showFilters && (
            <>
              <div
                className="fixed inset-0 z-[999] bg-black/20"
                onClick={() => setShowFilters(false)}
              />
              <div className="absolute top-16 left-4 right-4 z-[1000] bg-white rounded-xl shadow-xl p-4 max-h-[calc(100dvh-120px)] overflow-y-auto animate-in fade-in slide-in-from-top-2 duration-200">
                <MapFilterPanel onClose={() => setShowFilters(false)} />
              </div>
            </>
          )}

        {/* FAB - center position with safe area */}
        <div className="absolute bottom-[120px] right-4 z-[1000] safe-bottom" style={{ marginBottom: 'env(safe-area-inset-bottom, 0)' }}>
          <CenterMapButton onCenter={handleCenterMap} />
        </div>

        {/* Selected listing panel with safe area */}
        {selectedListing && (
          <div className="safe-bottom" style={{ marginBottom: 'env(safe-area-inset-bottom, 0)' }}>
            <SelectedListingPanelMobile
              listing={selectedListing}
              onClose={() => setSelectedListing(null)}
            />
          </div>
        )}

        {/* Bottom sheet - draggable */}
        <div
          ref={sheetRef}
          className={cn(
            "absolute left-0 right-0 bg-white rounded-t-2xl shadow-[0_-4px_20px_rgba(0,0,0,0.15)] z-[500]",
            isDragging ? '' : 'transition-transform duration-300 ease-out'
          )}
          style={{
            height: '100%',
            transform: `translateY(${sheetTranslateY}px)`,
            top: 0,
            touchAction: 'none',
          }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Handle for dragging */}
          <div className="sticky top-0 bg-white rounded-t-2xl pt-3 pb-3 px-4 border-b z-10">
            <div className="w-12 h-1.5 bg-gray-300 rounded-full mx-auto mb-3" />
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-lg">
                {mapListings.length} {mapListings.length === 1 ? 'annuncio' : 'annunci'}
              </h2>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="min-w-[44px] min-h-[44px] p-2 hover:bg-gray-100 rounded-lg transition-colors flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                aria-label="Toggle filtri"
              >
                <SlidersHorizontal className="h-5 w-5 text-gray-600" />
                {activeFiltersCount > 0 && (
                  <span className="ml-1 rounded-full bg-blue-500 px-1.5 py-0.5 text-xs text-white">
                    {activeFiltersCount}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* Listings list - scrollable with safe area */}
          <div
            className="overflow-y-auto px-4 pb-[140px] safe-bottom"
            style={{ height: 'calc(100% - 80px)', paddingBottom: 'max(140px, calc(140px + env(safe-area-inset-bottom)))' }}
          >
            {isLoading && mapListings.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <Spinner size="lg" />
              </div>
            ) : error && mapListings.length === 0 ? (
              <div className="text-center py-8">
                <AlertCircle className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500 mb-3">{error}</p>
                <Button variant="outline" size="sm" onClick={handleRetry}>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Riprova
                </Button>
              </div>
            ) : mapListings.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>Nessun annuncio in questa zona</p>
                {activeFiltersCount > 0 && (
                  <p className="text-sm mt-2">Prova a rimuovere alcuni filtri</p>
                )}
              </div>
            ) : (
              <div className="space-y-3 pt-2">
                {mapListings.map((listing, index) => (
                  <SimpleListingCard
                    key={listing.id || `map-listing-${index}`}
                    listing={listing}
                    isSelected={listing.id === selectedListingId}
                    onClick={() => handleListingClick(listing)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* DESKTOP LAYOUT */}
      <div className="hidden md:flex h-[calc(100vh-4rem)] overflow-hidden">
        {/* Map 60% */}
        <div className="w-[60%] relative">
          <MapContainer
            center={initialCenter}
            zoom={initialZoom}
            className="h-full w-full"
            zoomControl={false}
            ref={mapRef}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            <MapEvents onBoundsChange={handleBoundsChange} />
            <FlyToCity city={filters.city} />

            {mapListings.map((listing, index) => (
              <Marker
                key={listing.id || `map-listing-${index}`}
                position={[listing.latitude, listing.longitude]}
                icon={createPriceMarker(listing, listing.id === selectedListingId)}
                eventHandlers={{
                  click: () => setSelectedListing(listing.id),
                }}
              >
                <Popup>
                  <div className="min-w-[200px]">
                    <h3 className="font-semibold text-sm mb-1 line-clamp-2">
                      {listing.title}
                    </h3>
                    {listing.price ? (
                      <p className="font-bold text-blue-500">
                        {formatPrice(listing.price, listing.contractType)}
                      </p>
                    ) : (
                      <p className="text-sm text-gray-500">Prezzo su richiesta</p>
                    )}
                    <Link
                      to={`/listings/${listing.id}`}
                      className="text-sm text-blue-500 hover:underline mt-2 block"
                    >
                      Vedi dettagli
                    </Link>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>

          {/* Error banner */}
          {error && (
            <ErrorBanner message={error} onRetry={handleRetry} />
          )}

          {/* Floating filters - top left */}
          <div className="absolute top-4 left-4 z-[1000] flex gap-2">
            <Button
              variant="secondary"
              className="shadow-md bg-white"
              onClick={() => setShowFilters(!showFilters)}
            >
              <SlidersHorizontal className="mr-2 h-4 w-4" />
              Filtri
              <ActiveFiltersBadge count={activeFiltersCount} />
            </Button>
            <Link to="/listings">
              <Button variant="secondary" className="shadow-md bg-white">
                <List className="mr-2 h-4 w-4" />
                Lista
              </Button>
            </Link>
          </div>

          {/* Loading indicator - top right */}
          {isLoading && (
            <div className="absolute top-4 right-4 z-[1000] bg-white rounded-md shadow-md px-3 py-2 flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
              <span className="text-sm text-gray-600">Caricamento...</span>
            </div>
          )}

          {/* Results count - below loading or top right if not loading */}
          {!isLoading && !error && (
            <div className="absolute top-4 right-4 z-[1000] bg-white rounded-md shadow-md px-3 py-2">
              <span className="text-sm text-gray-600">
                {mapListings.length} {mapListings.length === 1 ? 'annuncio' : 'annunci'}
              </span>
            </div>
          )}

          {/* Filters panel with backdrop */}
          {showFilters && (
            <>
              <div
                className="fixed inset-0 z-[999] bg-black/10"
                onClick={() => setShowFilters(false)}
              />
              <div className="absolute top-16 left-4 z-[1000] bg-white rounded-xl shadow-xl p-4 max-h-[calc(100vh-120px)] overflow-y-auto animate-in fade-in slide-in-from-top-2 duration-200">
                <MapFilterPanel onClose={() => setShowFilters(false)} />
              </div>
            </>
          )}

          {/* FAB - center position */}
          <div className="absolute bottom-4 right-4 z-[1000]">
            <CenterMapButton onCenter={handleCenterMap} />
          </div>

          {/* Selected listing panel */}
          {selectedListing && (
            <SelectedListingPanelDesktop
              listing={selectedListing}
              onClose={() => setSelectedListing(null)}
            />
          )}
        </div>

        {/* Listings panel 40% */}
        <div className="w-[40%] bg-white overflow-y-auto border-l border-gray-200">
          {/* Header */}
          <div className="sticky top-0 bg-white border-b z-10 px-4 py-3 flex items-center justify-between">
            <div>
              <h2 className="font-semibold text-lg">
                {mapListings.length} {mapListings.length === 1 ? 'annuncio' : 'annunci'}
              </h2>
              {activeFiltersCount > 0 && (
                <p className="text-xs text-gray-500">
                  {activeFiltersCount} {activeFiltersCount === 1 ? 'filtro attivo' : 'filtri attivi'}
                </p>
              )}
            </div>
            {isLoading && <Spinner size="sm" />}
          </div>

          {/* Listings */}
          <div className="p-4 space-y-4">
            {isLoading && mapListings.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <Spinner size="lg" />
              </div>
            ) : error && mapListings.length === 0 ? (
              <div className="text-center py-12">
                <AlertCircle className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500 mb-3">{error}</p>
                <Button variant="outline" size="sm" onClick={handleRetry}>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Riprova
                </Button>
              </div>
            ) : mapListings.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <p>Nessun annuncio in questa zona</p>
                {activeFiltersCount > 0 && (
                  <p className="text-sm mt-2">Prova a rimuovere alcuni filtri</p>
                )}
              </div>
            ) : (
              mapListings.map((listing, index) => (
                <SimpleListingCard
                  key={listing.id || `map-listing-${index}`}
                  listing={listing}
                  isSelected={listing.id === selectedListingId}
                  onClick={() => handleListingClick(listing)}
                />
              ))
            )}
          </div>
        </div>
      </div>
    </>
  );
}
